// Client-side vector math for the vibe pad (mirrors apps/pictufy/src/lib/vectorSearch.ts).
// Anchors are embedded once server-side; drag-time blending happens here so the
// encoder Lambda is not hit per drag-step.

export function unit(v: number[]): number[] {
  let norm = 0;
  for (const x of v) norm += x * x;
  norm = Math.sqrt(norm) || 1;
  return v.map((x) => x / norm);
}

/** A semantic axis: the direction of meaning from text A toward text B
 *  (`king − man + woman` arithmetic). */
export function axisDirection(a: number[], b: number[]): number[] {
  const ua = unit(a);
  const ub = unit(b);
  return unit(ub.map((x, i) => x - ua[i]!));
}

/** Axis strength: how far one full slider notch (t = ±1) moves the query. */
export const AXIS_ALPHA = 0.6;

/** Compose the current query with the mixer axes: unit(base + Σ t·α·dir).
 *  With no base, the direction sum alone is searched (each axis's "extreme").
 *  Returns null when there is nothing to search at all. */
export function applyAxes(
  base: number[] | null,
  axes: { dir: number[]; t: number }[],
  alpha = AXIS_ALPHA
): number[] | null {
  const active = axes.filter((a) => a.t !== 0);
  if (!base && active.length === 0) return null;
  const dim = base?.length ?? active[0]!.dir.length;
  const out = base ? unit(base).slice() : new Array<number>(dim).fill(0);
  for (const a of active) {
    for (let i = 0; i < dim; i++) out[i]! += a.t * alpha * a.dir[i]!;
  }
  return unit(out);
}

/** Bilinear blend of 4 corner vectors (TL, TR, BL, BR) at pad position (x, y) ∈ [0,1]². */
export function blendBilinear(
  corners: [number[], number[], number[], number[]],
  x: number,
  y: number
): number[] {
  const w = [(1 - x) * (1 - y), x * (1 - y), (1 - x) * y, x * y];
  const u = corners.map(unit);
  const dim = u[0]!.length;
  const out = new Array<number>(dim).fill(0);
  for (let c = 0; c < 4; c++) {
    const uc = u[c]!;
    const wc = w[c]!;
    for (let i = 0; i < dim; i++) out[i]! += wc * uc[i]!;
  }
  return unit(out);
}
